import { FC, HTMLAttributes, useState } from "react";
import { useStyles } from "./AchievementDetailDialog.styles";
import { StaticImage } from "../StaticImage";
import { Typography } from "../../core/Typography/Typography";
import { ProgressBar } from "../ProgressBar";
import {
  TypographyColor,
  TypographyVariant,
} from "../../../constants/typography";
import { Achievement } from "@onmo/graphql/graphql";
import { convertAchievement } from "../../../models/achievement";
import { Dialog } from "../../core/Dialog/Dialog";
import Button from "../../core/Button/Button";
import { AchievementShareDialog } from "../AchievementShareDialog";
import { useTranslation } from "react-i18next";

interface IAchievementDetailDialogProps {
  open: boolean;
  achievement: Achievement;
  onClose: () => void;
}

export const AchievementDetailDialog: FC<
  IAchievementDetailDialogProps & HTMLAttributes<HTMLDivElement>
> = ({ open, achievement, onClose }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [openShare, setOpenShare] = useState(false);
  const {
    achievementName,
    achievementImage,
    progress,
    color,
    description,
    nextTierTarget,
    progressPercentage,
    currentTier,
    currentTierTarget,
    nextTierIcon,
  } = convertAchievement(achievement);

  return (
    <>
      <AchievementShareDialog
        achievement={achievement}
        open={openShare && open}
        onClose={() => setOpenShare(false)}
      />
      <Dialog className={classes.root} open={!openShare && open}>
        <div className={classes.content}>
          <StaticImage
            src={achievementImage?.src1x}
            src2x={achievementImage?.src2x}
            src3x={achievementImage?.src3x}
            className={classes.currentImage}
          />
          <div className={classes.textContainer}>
            <Typography
              variant={TypographyVariant.HeadingH5Bold}
              color={TypographyColor.Default}
              className={classes.achievementTitle}
            >
              {t(achievementName || "")}
            </Typography>
            {currentTier > 0 ? (
              <>
                <Typography
                  variant={TypographyVariant.BodyB5Regular}
                  color={TypographyColor.Silver}
                >
                  {t("common_Tier")} {currentTier}
                </Typography>
                <Typography
                  variant={TypographyVariant.BodyB5Regular}
                  color={TypographyColor.Silver}
                >
                  {t(description || "", {
                    amount: currentTierTarget || progress,
                  })}
                </Typography>
              </>
            ) : (
              <Typography
                variant={TypographyVariant.BodyB5Regular}
                color={TypographyColor.Silver}
              >
                {t("common_Locked")}
              </Typography>
            )}
          </div>
        </div>
        {!!nextTierIcon && (
          <div className={classes.nextTier}>
            <div className={classes.nextTierWrapper}>
              <StaticImage
                src={nextTierIcon.src1x}
                src2x={nextTierIcon.src2x}
                src3x={nextTierIcon.src3x}
                className={classes.nextTierImage}
              />
              <div className={classes.nextTierContent}>
                <Typography
                  variant={TypographyVariant.BodyB5Bold}
                  color={TypographyColor.Silver}
                  className={classes.nextTierTitle}
                >
                  {t("common_Next Tier")}
                </Typography>
                {!!nextTierTarget && (
                  <Typography variant={TypographyVariant.BodyB6Regular}>
                    {t(description || "", { amount: nextTierTarget })}
                  </Typography>
                )}
              </div>
            </div>
          </div>
        )}
        <div className={classes.progress}>
          <ProgressBar
            color={color}
            className={classes.progressBar}
            progress={progressPercentage}
            size="medium"
          />
          <Typography variant={TypographyVariant.BodyB6Regular}>
            {nextTierTarget ? `${progress}/${nextTierTarget}` : `${progress}`}
          </Typography>
        </div>
        <div className={classes.achievementDetailButtons}>
          <Button
            variant={currentTier <= 0 ? "contained" : "outlined"}
            size="large"
            fullWidth
            onClick={onClose}
            className={classes.btnContent}
          >
            {t("common_Close")}
          </Button>
          {currentTier > 0 && (
            <Button
              variant="contained"
              // color="primary"
              size="large"
              fullWidth
              onClick={() => setOpenShare(true)}
              className={classes.btnContent}
            >
              {t("common_Share")}
            </Button>
          )}
        </div>
      </Dialog>
    </>
  );
};
